import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Bot, Copy, Eye, EyeOff, Loader2, RefreshCw, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

type AssistantCredentialUser = {
  id: number;
  email: string;
  name: string | null;
  assistantName: string | null;
  hasAssistantPassword: boolean;
};

type RevealResponse = {
  hasPassword: boolean;
  password: string | null;
  assistantName: string | null;
  email: string;
};

function RevealRow({ user }: { user: AssistantCredentialUser }) {
  const { toast } = useToast();
  const [revealed, setRevealed] = useState<RevealResponse | null>(null);
  const [show, setShow] = useState(false);

  const revealMutation = useMutation({
    mutationFn: async () =>
      (await apiRequest("POST", `/api/admin/assistant-credentials/${user.id}/reveal`)).json() as Promise<RevealResponse>,
    onSuccess: (data) => {
      setRevealed(data);
      setShow(true);
    },
    onError: (err) =>
      toast({
        title: "Couldn't reveal password",
        description: parseErrorMessage(err, "Failed to decrypt the assistant password. Please try again."),
        variant: "destructive",
      }),
  });

  async function copyToClipboard(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: "Copied", description: "Assistant password copied to clipboard." });
    } catch {
      toast({ title: "Copy failed", description: "Couldn't access the clipboard.", variant: "destructive" });
    }
  }

  return (
    <tr className="border-t align-top" data-testid={`row-assistant-cred-${user.id}`}>
      <td className="px-3 py-2">
        <div className="font-medium" data-testid={`text-assistant-name-${user.id}`}>
          {user.assistantName || <span className="italic text-muted-foreground">no assistant name</span>}
        </div>
        <div className="text-xs text-muted-foreground" data-testid={`text-assistant-owner-${user.id}`}>
          {user.name ? `${user.name} · ` : ""}{user.email}
        </div>
      </td>
      <td className="px-3 py-2">
        {!user.hasAssistantPassword ? (
          <span className="text-xs italic text-muted-foreground" data-testid={`text-no-password-${user.id}`}>
            No password stored
          </span>
        ) : !revealed ? (
          <span className="font-mono text-sm tracking-wider text-muted-foreground" data-testid={`text-masked-${user.id}`}>
            ••••••••
          </span>
        ) : revealed.hasPassword && revealed.password ? (
          <div className="flex items-center gap-2">
            <span
              className="font-mono text-sm break-all"
              data-testid={`text-revealed-password-${user.id}`}
            >
              {show ? revealed.password : "•".repeat(Math.min(revealed.password.length, 16))}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={() => setShow((s) => !s)}
              aria-label={show ? "Hide password" : "Show password"}
              data-testid={`button-toggle-show-${user.id}`}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={() => copyToClipboard(revealed.password!)}
              aria-label="Copy password"
              data-testid={`button-copy-password-${user.id}`}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <span className="text-xs italic text-muted-foreground" data-testid={`text-no-password-${user.id}`}>
            No password stored
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {user.hasAssistantPassword && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => revealMutation.mutate()}
            disabled={revealMutation.isPending}
            data-testid={`button-reveal-password-${user.id}`}
          >
            {revealMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            ) : (
              <Eye className="h-3.5 w-3.5 mr-1" />
            )}
            {revealed ? "Reveal again" : "Reveal"}
          </Button>
        )}
      </td>
    </tr>
  );
}

export function AssistantPasswordRevealPanel() {
  const [search, setSearch] = useState("");
  const listQuery = useQuery<AssistantCredentialUser[]>({ queryKey: ["/api/admin/assistant-credentials"] });

  const users = listQuery.data ?? [];
  const filtered = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(u.assistantName ?? "").toLowerCase().includes(q) ||
      String(u.name ?? "").toLowerCase().includes(q) ||
      String(u.email ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-start gap-2">
        <Bot className="h-5 w-5 text-brand mt-0.5 shrink-0" />
        <div>
          <h2 className="font-semibold text-base">Assistant Passwords</h2>
          <p className="text-xs text-muted-foreground">
            Reveal a user's assistant (bot) password in plaintext. The value is decrypted on demand and only after you
            click Reveal. Every reveal is recorded in the admin audit log. This is read-only — the password is owned by
            the warm-browser system and cannot be changed here.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-7 h-8 text-sm w-64"
            placeholder="Search by email or assistant name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="input-assistant-cred-search"
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => listQuery.refetch()}
          disabled={listQuery.isFetching}
          data-testid="button-refresh-assistant-creds"
        >
          {listQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {listQuery.isLoading ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-assistant-cred-results">
          {search.trim() ? "No users match your search." : "No users with an assistant identity found."}
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Assistant / Owner</th>
                <th className="text-left px-3 py-2">Password</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <RevealRow key={u.id} user={u} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
